/**
 * The studio map's chunky scene card (spec 025), shared by the Path and the
 * Fly-through looks: the thumbnail (D-5), the scene's name in large type, its
 * stop number in a round badge, a Start ribbon, its badges (D-6) and its
 * loop chips. An end node is a finish-flag card.
 *
 * With `onSelect` the card is a button (the Path); without it, a plain block
 * the fly-through positions and clicks itself.
 */

import React, { forwardRef } from 'react';
import type { Thumb } from './sceneThumbs';
import styles from './StoryStudio.module.scss';

export interface SceneCardProps {
  kind: 'scene' | 'end';
  name: string;
  stop?: number;
  thumb?: Thumb;
  /** The decoded image URL of the thumbnail's media, once loaded. */
  thumbSrc?: string;
  isStart?: boolean;
  selected?: boolean;
  missingMedia?: boolean;
  needsFix?: boolean;
  /** Names of the scenes this one loops back to. */
  loops?: readonly string[];
  small?: boolean;
  onSelect?: () => void;
  testId?: string;
}

const ThumbView: React.FC<{ thumb?: Thumb; src?: string }> = ({ thumb, src }) => {
  if (!thumb) return <div className={styles.thumb} />;
  if (thumb.kind === 'image') {
    return (
      <div className={styles.thumb} style={{ background: thumb.color }}>
        <img className={styles.thumbImage} src={src} alt="" data-testid="scene-card-image" />
      </div>
    );
  }
  if (thumb.kind === 'character') {
    return (
      <div className={styles.thumb} style={{ background: thumb.color }}>
        <img className={styles.thumbCharacter} src={src} alt="" data-testid="scene-card-character" />
      </div>
    );
  }
  return (
    <div className={`${styles.thumb} ${styles.thumbTile}`} style={{ background: thumb.color }} data-testid="scene-card-initial">
      {thumb.initial}
    </div>
  );
};

export const SceneCard = forwardRef<HTMLElement, SceneCardProps>(function SceneCard(props, ref) {
  const {
    kind, name, stop, thumb, thumbSrc, isStart, selected, missingMedia, needsFix, loops, small, onSelect, testId,
  } = props;
  const className = [
    styles.card,
    kind === 'end' ? styles.endCard : '',
    selected ? styles.cardSelected : '',
    small ? styles.cardSmall : '',
  ].join(' ');

  const body = kind === 'end' ? (
    <>
      <div className={styles.endFlag} aria-hidden="true">🏁</div>
      <div className={styles.cardName}>The end</div>
    </>
  ) : (
    <>
      {isStart && <span className={styles.startRibbon}>Start</span>}
      <ThumbView thumb={thumb} src={thumbSrc} />
      <div className={styles.cardFoot}>
        {stop !== undefined && <span className={styles.stopBadge} aria-label={`Scene ${stop}`}>{stop}</span>}
        <span className={styles.cardName}>{name}</span>
      </div>
      {(missingMedia || needsFix) && (
        <div className={styles.cardBadges}>
          {missingMedia && <span className={styles.badgeMissing} data-testid="badge-missing">Missing media</span>}
          {needsFix && <span className={styles.badgeFix} data-testid="badge-fix">Needs a fix</span>}
        </div>
      )}
      {loops?.map((to, i) => (
        <span key={i} className={styles.loopChip} data-testid="loop-chip">↺ back to {to}</span>
      ))}
    </>
  );

  if (onSelect) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={className}
        onClick={onSelect}
        aria-pressed={!!selected}
        aria-label={kind === 'end' ? 'The end' : `${name}${stop !== undefined ? `, scene ${stop}` : ''}`}
        data-testid={testId}
      >
        {body}
      </button>
    );
  }
  return (
    <div ref={ref as React.Ref<HTMLDivElement>} className={className} data-testid={testId} data-selected={selected ? 'true' : 'false'}>
      {body}
    </div>
  );
});

export default SceneCard;
